import { useContext, useEffect, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client/react/hooks/useApolloClient';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';

import { getDefaultSearchLocation } from 'src/common/constants/locations';
import { CountryCodes } from 'src/common/enums';
import { ROUTES } from 'src/common/routes';
import {
  DefaultLocation,
  NewSearchLocation,
} from 'src/global/models/NewSearchLocation';
import { getCountryWithSGFallback } from 'src/modules/ClientConfig/Selectors';
import { SearchRouterQueryKey } from 'src/modules/Opportunities/pages/Explore/types';
import { HierarchicalLocation } from 'src/modules/Profile/graphql/hierarchicalLocations';

import { findHierarchicalLocationBySlug } from '../../../graphql';
import { getFilterSlugValue } from '../../../helpers';
import { SEOSlugUrlParams } from '../../FilterSection/context';
import { useSearchLocation } from './useSearchLocation';

export const useLocationSearchValues = () => {
  const router = useRouter();
  const countryCode = useSelector(getCountryWithSGFallback);
  const { slugQuery } = useContext(SEOSlugUrlParams);
  const client = useApolloClient();
  const defaultSearch = getDefaultSearchLocation(countryCode);

  const currentPath = router.pathname;
  const isLandingPage = currentPath === '/';
  const isInternalSearchExplorePage = currentPath.includes(
    ROUTES.exploreInternalSearchAndFilters
  );
  const { [SearchRouterQueryKey.Filter]: keywordsQuery = '' } = router.query;
  const { slug, isLocationSlug } = getFilterSlugValue(keywordsQuery);
  const hasLocationFilterInInternalSearch =
    isInternalSearchExplorePage && isLocationSlug;

  const location: HierarchicalLocation | undefined =
    hasLocationFilterInInternalSearch
      ? client.readQuery({
          query: findHierarchicalLocationBySlug,
          variables: {
            slug: slug,
          },
        })?.findHierarchicalLocationBySlug
      : undefined;
  const locationId = location?.id || '';
  const locationFormattedName = useMemo(
    () => location?.formattedName,
    [location?.formattedName]
  );

  const locationNameQueryParam =
    hasLocationFilterInInternalSearch && locationFormattedName
      ? locationFormattedName
      : ((router.query[SearchRouterQueryKey.Location] ??
          (slugQuery && slugQuery[SearchRouterQueryKey.Location])) as string);
  const locationIdQueryParam = hasLocationFilterInInternalSearch
    ? locationId
    : ((router.query[SearchRouterQueryKey.LocationId] ??
        (slugQuery && slugQuery[SearchRouterQueryKey.LocationId])) as string);
  const searchCityId = router.query[SearchRouterQueryKey.City] as string;
  const countryCodeQuery = router.query[
    SearchRouterQueryKey.Country
  ] as CountryCodes;

  const { searchLocation, loading } = useSearchLocation(
    locationIdQueryParam,
    searchCityId
  );

  const defaultSearchLocation = isLandingPage
    ? DefaultLocation.getDefaultLocationAll(countryCode)
    : searchLocation;

  /** search input value is the display label on search input.
   * When default location is selected (including when URL has default in
   * locationNameQueryParam), use empty string so the default label shows
   * as placeholder (user can type immediately on focus)
   */
  const getInitialLocationInputValue = () => {
    if (
      locationNameQueryParam &&
      locationNameQueryParam !== defaultSearch.name
    ) {
      return locationNameQueryParam;
    }
    if (defaultSearchLocation.label === defaultSearch.name) {
      return '';
    }
    return defaultSearchLocation.label;
  };

  const [locationInputValue, setLocationInputValue] = useState<string>(
    getInitialLocationInputValue
  );

  const [locationSearchValue, setLocationSearchValue] =
    useState<NewSearchLocation>(defaultSearchLocation);

  const [selectedCountry, setSelectedCountry] = useState<CountryCodes>(
    countryCodeQuery || countryCode
  );

  useEffect(() => {
    if (countryCodeQuery) {
      setSelectedCountry(countryCodeQuery);
    }
  }, [countryCodeQuery]);

  useEffect(
    function syncKeywordWhenExploreTabLocationQueryUpdates() {
      if (!isLandingPage) {
        setLocationSearchValue(searchLocation);
        /** use url locationNameQueryParam until location is loaded*/
        if (!loading) {
          setLocationInputValue(
            searchLocation.label === defaultSearch.name
              ? ''
              : searchLocation.label
          );
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLandingPage, searchLocation.label]
  );

  const handleLocationSearchValueChange = (location: NewSearchLocation) => {
    setLocationSearchValue(location);
    setLocationInputValue(location.label);
  };

  const handleChangeSelectedCountry = (country: CountryCodes) => {
    /** clear city/province when country changed */
    const defaultLocation = DefaultLocation.getDefaultLocationAll(country);
    const newCountryDefaultSearch = getDefaultSearchLocation(country);
    setSelectedCountry(country);
    setLocationSearchValue(defaultLocation);
    setLocationInputValue(
      hasLocationFilterInInternalSearch && locationFormattedName
        ? locationFormattedName
        : defaultLocation.label === newCountryDefaultSearch.name
        ? ''
        : defaultLocation.label
    );
  };

  return {
    locationSearchValue,
    selectedCountry,
    locationInputValue,
    setLocationSearchValue: handleLocationSearchValueChange,
    setSelectedCountry: handleChangeSelectedCountry,
    setLocationInputValue,
  };
};
